import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { LogoSymbol, LogoWordmark, LogoTagline } from "./Logo";
import { api } from "../lib/api";

const PRODUCT = [
  { label: "Compatibility checker", href: "/check" },
  { label: "Categories", href: "/categories" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "/faq" },
];

const COMPANY = [
  { label: "About", href: "/about" },
  { label: "Methodology", href: "/methodology" },
  { label: "Contact", href: "mailto:hello@blendsafe.com" },
];

const LEGAL = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Disclaimer", href: "/disclaimer" },
  { label: "Cookies", href: "/cookies" },
];

function FooterColumn({ heading, items }) {
  return (
    <div data-testid={`footer-col-${heading.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.24em] text-ink-mute font-bold">
        {heading}
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <li key={it.label}>
            <a
              href={it.href}
              data-testid={`footer-link-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="group inline-flex items-center gap-1.5 text-[13.5px] text-ink-soft hover:text-ink transition-colors"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <span className="group-hover:translate-x-0.5 transition-transform">
                {it.label}
              </span>
              <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter/subscribe", { email: email.trim() });
      toast.success("You're on the list — we'll be in touch.");
      setEmail("");
    } catch (_err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="footer-newsletter" className="mt-6">
      <div className="text-[10px] uppercase tracking-[0.24em] text-ink-mute font-bold inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-peach-500" />
        Monthly insights
      </div>
      <p className="text-[13px] text-ink-soft/85 mt-2 leading-relaxed max-w-xs">
        New ingredient interactions, drug-label updates. One short email a month.
      </p>
      <div className="mt-3 flex items-stretch gap-2 rounded-full bg-white border border-surface-line p-1 max-w-md focus-within:ring-4 focus-within:ring-peach-100 focus-within:border-peach-200 transition-all">
        <input
          type="email"
          required
          data-testid="footer-newsletter-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="flex-1 min-w-0 px-4 bg-transparent text-[16px] text-ink placeholder:text-ink-light focus:outline-none"
        />
        <button
          type="submit"
          data-testid="footer-newsletter-submit"
          disabled={submitting}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[12.5px] font-semibold transition-all hover:scale-[1.04] active:scale-[0.96] disabled:opacity-70"
        >
          {submitting ? "Sending…" : "Subscribe"}
          {!submitting && <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </div>
    </form>
  );
}

export default function SiteFooter() {
  const [year] = useState(new Date().getFullYear());
  const { pathname } = useLocation();
  const WORKFLOW = ["/account", "/routines", "/routine", "/check"];
  const compact = WORKFLOW.some((p) => pathname === p || pathname.startsWith(p + "/"));

  if (compact) {
    return (
      <footer
        data-testid="site-footer-compact"
        className="border-t border-surface-line bg-cream-50"
      >
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <Link to="/" data-testid="footer-logo-compact" className="inline-flex items-center gap-2.5">
            <LogoSymbol size={28} color="#111111" />
            <LogoWordmark size={18} />
          </Link>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-ink-soft">
            <a href="mailto:hello@blendsafe.com" className="hover:text-ink transition-colors" data-testid="footer-compact-support">Support</a>
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-ink transition-colors">Terms</Link>
            <Link to="/disclaimer" className="hover:text-ink transition-colors">Disclaimer</Link>
          </nav>
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-ink-mute">
            Informational tool · Not medical advice
          </span>
        </div>
      </footer>
    );
  }

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-surface-line bg-cream-50"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12">
        {/* Brand + newsletter column */}
        <div className="lg:col-span-5 min-w-0">
          <Link to="/" data-testid="footer-logo" className="inline-flex items-center group" style={{ gap: 20 }}>
            <LogoSymbol
              size={64}
              color="#111111"
              className="transition-transform duration-500 group-hover:rotate-[-6deg]"
            />
            <span className="flex flex-col" style={{ gap: 6 }}>
              <LogoWordmark size={36} />
              <LogoTagline size={12} />
            </span>
          </Link>
          <NewsletterForm />
        </div>

        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8">
          <FooterColumn heading="Product" items={PRODUCT} />
          <FooterColumn heading="Company" items={COMPANY} />
          <FooterColumn heading="Legal" items={LEGAL} />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-surface-line">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <span className="text-[12px] text-ink-mute">
            © {year} Lulo Software LLC · Built for US consumers
          </span>
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-ink-mute">
            Informational tool · Not medical advice
          </span>
        </div>
      </div>
    </footer>
  );
}
